import React, { useState } from 'react';
import { Typography, Row, Col, Card, Divider, message } from 'antd';
import { 
  processImage, 
  getImageDimensions, 
  fileToDataUrl, 
  downloadImage,
  ImageProcessOptions 
} from '../../utils/imageProcessor';
import ImageUploader from '../../components/ImageUploader';
import ImagePreview from '../../components/ImagePreview';
import ImageProcessorForm from '../../components/ImageProcessorForm';
import ProcessedImagePreview from '../../components/ProcessedImagePreview';

const { Title, Paragraph } = Typography;

const ImageProcessor: React.FC = () => {
  // 原始图片状态
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [originalPreview, setOriginalPreview] = useState<string | null>(null);
  const [originalDimensions, setOriginalDimensions] = useState<{ width: number; height: number } | null>(null);
  
  // 处理后的图片状态
  const [processedFile, setProcessedFile] = useState<File | null>(null);
  const [processedPreview, setProcessedPreview] = useState<string | null>(null);
  const [processedDimensions, setProcessedDimensions] = useState<{ width: number; height: number } | null>(null);
  
  // 加载状态
  const [processing, setProcessing] = useState(false);

  // 处理图片上传
  const handleImageSelected = async (file: File, dataUrl: string) => {
    setOriginalFile(file);
    setOriginalPreview(dataUrl);
    
    try {
      // 获取图片尺寸
      const dimensions = await getImageDimensions(file);
      setOriginalDimensions(dimensions);
    } catch (error) {
      console.error('获取图片尺寸失败:', error);
    }
    
    // 清除之前处理的图片
    setProcessedFile(null);
    setProcessedPreview(null);
    setProcessedDimensions(null);
  };

  // 处理图片
  const handleProcessImage = async (options: ImageProcessOptions) => {
    if (!originalFile) {
      message.error('请先上传图片');
      return;
    }
    
    setProcessing(true);
    
    try {
      // 处理图片
      const processed = await processImage(originalFile, options);
      setProcessedFile(processed);
      
      // 生成预览
      const preview = await fileToDataUrl(processed);
      setProcessedPreview(preview);
      
      // 获取处理后的图片尺寸
      const dimensions = await getImageDimensions(processed);
      setProcessedDimensions(dimensions);
      
      message.success('图片处理成功');
    } catch (error) {
      console.error('图片处理失败:', error);
      message.error('图片处理失败');
    } finally {
      setProcessing(false);
    }
  };

  // 下载处理后的图片
  const handleDownload = () => {
    if (processedFile) {
      downloadImage(processedFile);
    }
  };

  // 清除图片
  const handleClearImage = () => {
    setOriginalFile(null);
    setOriginalPreview(null);
    setOriginalDimensions(null);
    setProcessedFile(null);
    setProcessedPreview(null);
    setProcessedDimensions(null);
  };

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        <Title level={2}>图片处理工具</Title>
        <Paragraph style={{ maxWidth: '800px', margin: '0 auto' }}>
          上传图片，调整分辨率，裁剪和压缩，然后下载处理后的图片。所有处理都在浏览器中完成，不会上传到服务器。
        </Paragraph>
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24} md={12}>
          <Card title="上传图片">
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <ImageUploader onImageSelected={handleImageSelected} />
            </div>
          </Card>
          
          {originalPreview && originalFile && (
            <div style={{ marginTop: '20px' }}>
              <ImagePreview 
                imageUrl={originalPreview}
                imageInfo={{
                  name: originalFile.name,
                  size: originalFile.size,
                  width: originalDimensions?.width,
                  height: originalDimensions?.height
                }}
                onClear={handleClearImage}
              />
            </div>
          )}
        </Col>
        
        <Col xs={24} md={12}>
          <Card title="图片处理选项">
            <ImageProcessorForm 
              onProcess={handleProcessImage}
              loading={processing}
              originalWidth={originalDimensions?.width}
              originalHeight={originalDimensions?.height}
            />
          </Card>
          
          {processedPreview && processedFile && (
            <div style={{ marginTop: '20px' }}>
              <ProcessedImagePreview 
                imageUrl={processedPreview}
                imageInfo={{
                  name: processedFile.name,
                  size: processedFile.size,
                  width: processedDimensions?.width,
                  height: processedDimensions?.height,
                  format: processedFile.type.split('/')[1]
                }}
                onDownload={handleDownload}
              />
            </div>
          )}
        </Col>
      </Row>

      <Divider />
      
      <div style={{ marginTop: '40px' }}>
        <Title level={4}>使用说明</Title>
        <Paragraph>
          1. 点击"选择图片"按钮上传您要处理的图片。
        </Paragraph>
        <Paragraph>
          2. 设置目标分辨率、是否保持宽高比、图片质量和输出格式。
        </Paragraph>
        <Paragraph>
          3. 点击"处理图片"按钮进行处理。
        </Paragraph>
        <Paragraph>
          4. 处理完成后，您可以预览处理后的图片，并点击"下载"按钮保存到本地。
        </Paragraph>
      </div>
    </div>
  );
};

export default ImageProcessor;